import { uniqueId } from 'lodash/fp';
import { ComponentProps, ReactNode, useMemo } from 'react';
import { WrappedFieldProps } from 'redux-form';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import ReactSelect, { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';

const ReactSelectField = (
  props: WrappedFieldProps & ComponentProps<typeof ReactSelect> & { label?: ReactNode },
) => {
  const {
    input,
    meta: { error, submitFailed, touched },
    label,
    id,
    className,
    ...passthroughProps
  } = props;

  const hasError = !!(error && (submitFailed || touched));
  const labelId = useMemo(() => uniqueId('persona-react-select-field__'), []);

  return (
    <FormGroup className={className}>
      {label && <FormControlLabel id={labelId}>{label}</FormControlLabel>}
      <ReactSelect
        {...input}
        {...passthroughProps}
        id={id}
        hasError={hasError}
        // [PER-9753] Call Redux-form onBlur with `undefined` to fix mobile devices not being able to select values.
        // `blurInputOnSelect` defaults to `true` on mobile devices, which causes the blur event to fire once an option
        // is clicked. Because the change action has not resolved yet, `input.value` is still `''`, so the blur event
        // will cause the selection to be a no-op.
        // https://github.com/JedWatson/react-select/issues/2692#issuecomment-488541149
        onBlur={() => input.onBlur(undefined)}
        aria-labelledby={label ? labelId : undefined}
      />
      {hasError && <FormError>{error}</FormError>}
    </FormGroup>
  );
};

export default ReactSelectField;

export interface ReactCreatableSelectFieldProps
  extends WrappedFieldProps,
    ComponentProps<typeof ReactCreatableSelect> {
  label?: ReactNode;
}

export const ReactCreatableSelectField = (props: ReactCreatableSelectFieldProps) => {
  const {
    input,
    meta: { error, submitFailed, touched },
    label,
    id,
    ...passthroughProps
  } = props;

  const hasError = !!(error && (submitFailed || touched));
  const labelId = useMemo(() => uniqueId('persona-react-select-creatable-field__'), []);

  return (
    <FormGroup>
      {label && <FormControlLabel id={labelId}>{label}</FormControlLabel>}
      <ReactCreatableSelect
        {...input}
        {...passthroughProps}
        id={id}
        hasError={hasError}
        // [PER-9753] Call Redux-form onBlur with `undefined` to fix mobile devices not being able to select values.
        // `blurInputOnSelect` defaults to `true` on mobile devices, which causes the blur event to fire once an option
        // is clicked. Because the change action has not resolved yet, `input.value` is still `''`, so the blur event
        // will cause the selection to be a no-op.
        // https://github.com/JedWatson/react-select/issues/2692#issuecomment-488541149
        onBlur={() => input.onBlur(undefined)}
        aria-labelledby={label ? labelId : undefined}
      />
      {hasError && <FormError>{error}</FormError>}
    </FormGroup>
  );
};
